/**
 * The opener's theme on a Shopify shop (`shopify_shops.opener_theme`), resolved for the storefront
 * overlay and for the two editors' previews (the dashboard's Shopify page and the embedded admin).
 *
 * Stored in the opener's own shape, seven color roles plus rarity, radius, font and the variables
 * of the kind the shop opens with, not in storefront tokens: the overlay has no page theme to
 * follow, and a merchant edits what the opener paints. Zod-free, so the overlay bundle imports it by path
 * (`@loot-os/api-contract/shopifyOpenerTheme`) without pulling zod in; the schema is
 * `shopifyOpenerThemeSchema` in the barrel.
 */
import { isHexColor } from "./color.js";
import { OPENER_COLOR_ROLES, type OpenerColorRole, type OpenerThemeLike } from "./openerTheme.js";
import { STOREFRONT_RARITY_TIERS, type StorefrontRarityKey } from "./rarityTiers.js";
import { safeThemeColor, safeThemeFontFamily, safeThemeRadius } from "./storefrontThemeVars.js";

/** A stored Shopify opener theme. Every field is optional; an unset one keeps its default. */
export interface ShopifyOpenerTheme {
  colors?: Partial<Record<OpenerColorRole, string>>;
  rarity?: Partial<Record<StorefrontRarityKey, string>>;
  radius?: string;
  fontFamily?: string;
  /**
   * The opener kind's own variables (`OPENER_KIND_TOKENS`), hex colors keyed by variable: the
   * editors save those of the kind the shop's style opens with. They sit under the style's own,
   * like every other value here.
   */
  kind?: Record<string, string>;
}

/** Longest a rarity override may be: the value reaches an inline style. */
const MAX_RARITY_VALUE_LENGTH = 30;

/** Below this luminance the shop's brand color is unreadable on the opener's dark surface. */
const MIN_BRAND_ACCENT_LUMINANCE = 0.12;

/** A kind variable's key, as `openerThemeOverridesSchema` accepts it. */
const KIND_TOKEN_KEY = /^[a-z][a-zA-Z0-9]{0,31}$/;

/** Above this luminance text on the accent is dark. */
const DARK_TEXT_ABOVE_LUMINANCE = 0.4;

/** Relative luminance (0-1) of a `#rrggbb` color; null when it is not one. */
function luminanceOf(hex: string): number | null {
  const digits = /^#([0-9a-f]{6})$/i.exec(hex.trim())?.[1];
  if (!digits) return null;
  const channel = (offset: number) => {
    const value = Number.parseInt(digits.slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
}

/** Text that reads on a `#rrggbb` fill (black or white), or null when the fill is not one. */
export function foregroundOn(hex: string): string | null {
  const luminance = luminanceOf(hex);
  if (luminance === null) return null;
  return luminance > DARK_TEXT_ABOVE_LUMINANCE ? "#111111" : "#ffffff";
}

/** The shop's brand color as the accent, when it reads on the opener's dark surface. */
function brandAccentOf(brandColor: string | null | undefined): string | null {
  const luminance = brandColor ? luminanceOf(brandColor) : null;
  return brandColor && luminance !== null && luminance >= MIN_BRAND_ACCENT_LUMINANCE
    ? brandColor.trim()
    : null;
}

/**
 * The opener's theme on a shop, sanitized: the stored theme's colors, rarity, radius, font and
 * kind variables, and the shop's brand color as the accent wherever the merchant has not set one (only when it
 * reads on the opener's dark surface). A stored accent without its text color gets black or
 * white by contrast. Undefined when there is nothing to apply, which leaves the opener on its own
 * palette. Structural like `resolveOpenerTheme`, so this package stays free of the published one.
 */
export function resolveShopifyOpenerTheme(
  stored: ShopifyOpenerTheme | null | undefined,
  brandColor: string | null | undefined,
): OpenerThemeLike | undefined {
  const colors: Partial<Record<OpenerColorRole, string>> = {};
  // The role list, never the stored keys: a merchant supplies values, never variable names.
  for (const role of OPENER_COLOR_ROLES) {
    const value = safeThemeColor(stored?.colors?.[role]);
    if (value) colors[role] = value;
  }
  if (!colors.accent) {
    const brand = brandAccentOf(brandColor);
    if (brand) colors.accent = brand;
  }
  if (colors.accent && !colors.accentForeground) {
    const foreground = foregroundOn(colors.accent);
    if (foreground) colors.accentForeground = foreground;
  }

  const rarity: Partial<Record<number, string>> = {};
  for (const tier of STOREFRONT_RARITY_TIERS) {
    const value = stored?.rarity?.[tier.key];
    if (typeof value === "string" && value.length <= MAX_RARITY_VALUE_LENGTH) {
      const safe =
        safeThemeColor(value) ?? (/^\d{1,3},\d{1,3},\d{1,3}$/.test(value) ? value : null);
      if (safe) rarity[Number(tier.key)] = safe;
    }
  }

  const radius = safeThemeRadius(stored?.radius);
  const fontFamily = safeThemeFontFamily(stored?.fontFamily);

  const kind: Record<string, string> = {};
  for (const [key, value] of Object.entries(stored?.kind ?? {})) {
    if (KIND_TOKEN_KEY.test(key) && typeof value === "string" && isHexColor(value)) {
      kind[key] = value.trim();
    }
  }

  const theme: OpenerThemeLike = {
    ...(Object.keys(colors).length ? { colors } : {}),
    ...(Object.keys(rarity).length ? { rarity } : {}),
    ...(radius ? { radius } : {}),
    ...(fontFamily ? { fontFamily } : {}),
    ...(Object.keys(kind).length ? { kind } : {}),
  };
  return Object.keys(theme).length ? theme : undefined;
}

/** What each color role paints, as both editors label it. */
export const OPENER_COLOR_LABELS: Record<OpenerColorRole, string> = {
  background: "Background",
  surface: "Panels",
  border: "Borders",
  text: "Text",
  textMuted: "Secondary text",
  accent: "Buttons and highlights",
  accentForeground: "Text on buttons",
};

/**
 * The opener style select's value for "no style of its own, inherit the next level up", in both
 * editors; it never reaches the server, which takes null instead.
 */
export const OPENER_STYLE_INHERIT = "__inherit__";

/**
 * An opener theme form, as every opener theme editor holds it (the shop's two and the opener style
 * editor): every field a string, blank = unset.
 */
export interface ShopifyOpenerThemeDraft {
  colors: Partial<Record<OpenerColorRole, string>>;
  rarity: Partial<Record<StorefrontRarityKey, string>>;
  radius: string;
  fontFamily: string;
  /** The opener kind's own variables, keyed by variable. */
  kind: Record<string, string>;
}

/** The form for a stored theme; null = every field blank. */
export function shopifyOpenerThemeDraftOf(
  theme: ShopifyOpenerTheme | null,
): ShopifyOpenerThemeDraft {
  return {
    colors: theme?.colors ?? {},
    rarity: theme?.rarity ?? {},
    radius: theme?.radius ?? "",
    fontFamily: theme?.fontFamily ?? "",
    kind: theme?.kind ?? {},
  };
}

/**
 * An editor's form as the theme to save: blank fields dropped (an unset value keeps its default),
 * and null when nothing is left, which puts the shop back on the brand color.
 */
export function compactShopifyOpenerTheme(
  draft: ShopifyOpenerThemeDraft,
): ShopifyOpenerTheme | null {
  const pick = <T extends Partial<Record<string, string>>>(values: T): T | undefined => {
    const kept = Object.fromEntries(
      Object.entries(values as Record<string, string | undefined>)
        .map(([key, value]) => [key, value?.trim() ?? ""])
        .filter(([, value]) => value !== ""),
    ) as T;
    return Object.keys(kept).length ? kept : undefined;
  };
  const theme: ShopifyOpenerTheme = {};
  const colors = pick(draft.colors);
  const rarity = pick(draft.rarity);
  const kind = pick(draft.kind);
  if (colors) theme.colors = colors;
  if (rarity) theme.rarity = rarity;
  if (draft.radius.trim()) theme.radius = draft.radius.trim();
  if (draft.fontFamily.trim()) theme.fontFamily = draft.fontFamily.trim();
  if (kind) theme.kind = kind;
  return Object.keys(theme).length ? theme : null;
}
